import { lowercaseAndHyphen } from "app/utils/strings";
import * as React from "react";
import { Ref, Accordion as SemanticAccordion, StrictAccordionProps } from "semantic-ui-react";
import "./Accordion.scss";

export interface IAccordionPanel {
	id?: string;
	title: string;
	titleAsElement?: JSX.Element;
	titleWhenOpen?: string;
	content: any;
	icon?: JSX.Element;
}
interface IProps extends StrictAccordionProps {
	id: string;
	panels: IAccordionPanel[];
	selectedIndex?: number;
}

interface IState {
	clickedIndex: number;
	childHeight: number;
}

const ACCORDION_ANIMATION_TIME = 250;

class Accordion extends React.Component<IProps, IState> {
	public state: IState = {
		clickedIndex: -1,
		childHeight: 0,
	};

	public accordionRef: any = null;
	public handleRef = (node: any): void => {
		this.accordionRef = node;
	};

	public componentDidMount = (): void => {
		const { selectedIndex } = this.props;
		if (undefined !== selectedIndex && this.state.clickedIndex !== selectedIndex) {
			this.setState({ clickedIndex: selectedIndex });
		}
	};

	public componentDidUpdate = (): void => {
		if (undefined !== this.accordionRef) {
			let newChildHeight;
			for (const childNode of this.accordionRef.childNodes) {
				if (childNode.className === "content active") {
					newChildHeight = childNode.scrollHeight;
				}
			}

			if (newChildHeight !== this.state.childHeight && newChildHeight > this.state.childHeight) {
				this.setState({ childHeight: newChildHeight });
			}
		}
	};

	public render = (): JSX.Element => {
		const { id, panels } = this.props;
		const { clickedIndex, childHeight } = this.state;

		const accordionPanels = panels.map((panel: IAccordionPanel, index: number) => {
			const { title, titleAsElement, titleWhenOpen, icon, content, id } = panel;

			const key = id || `accordion--${lowercaseAndHyphen(title)}`;

			let active = false;
			let displayTitle = title;

			if (clickedIndex === index) {
				active = true;
				displayTitle = titleWhenOpen || displayTitle;
			}

			const titleComponent =
				titleAsElement !== undefined ? titleAsElement : generateTitleComponent(key, displayTitle, index, icon);
			const maxHeight = index === clickedIndex ? childHeight : 0;
			const titleClassName = titleAsElement !== undefined ? "title--as-element" : "";

			return {
				key,
				active,
				title: { content: titleComponent, className: titleClassName },
				content: { content, style: { maxHeight }, id: `${key}--content` },
			};
		});

		return (
			<div className="accordion-wrapper">
				<Ref innerRef={this.handleRef}>
					<SemanticAccordion id={id} panels={accordionPanels} onTitleClick={this.onTitleClick} />
				</Ref>
			</div>
		);
	};

	private onTitleClick = (event: React.MouseEvent<HTMLElement>, data: any): void => {
		const { nextElementSibling, previousElementSibling } = event.currentTarget;
		const { index } = data;
		let windowScrollPosition;

		if (!nextElementSibling) {
			return;
		}

		if (index === this.state.clickedIndex) {
			this.setState({ clickedIndex: -1, childHeight: 0 });
			return;
		}
		const offsetTopOfElementFromViewport = event.currentTarget.getBoundingClientRect().top;

		const windowYOffset = window.pageYOffset;

		if (previousElementSibling) {
			let pointToScrollTo;
			// if the previous accordion is opened, subtract its height
			if (previousElementSibling.className.includes("active")) {
				pointToScrollTo = offsetTopOfElementFromViewport + windowYOffset - previousElementSibling.scrollHeight;
			} else {
				pointToScrollTo = offsetTopOfElementFromViewport + windowYOffset;
			}
			if (pointToScrollTo > 0) {
				windowScrollPosition = pointToScrollTo;
			}
		} else {
			windowScrollPosition = offsetTopOfElementFromViewport + windowYOffset;
		}

		// scroll only if final position of panel is outside view
		if (windowYOffset > windowScrollPosition) {
			setTimeout(() => {
				window.scrollTo({
					left: 0,
					top: windowScrollPosition,
					behavior: "smooth",
				});
			}, ACCORDION_ANIMATION_TIME);
		}

		this.setState({
			clickedIndex: index,
			childHeight: nextElementSibling.scrollHeight,
		});
	};
}

const generateTitleComponent = (key: string, title: any, index: number, icon?: JSX.Element): JSX.Element => {
	if (icon) {
		return (
			<button className="title--with-icon" id={key}>
				<span>{title}</span>
				{icon}
			</button>
		);
	}

	const className = `accordion-panel title--text panel${index}`;

	return (
		<button className={className} id={key}>
			{title}
		</button>
	);
};

export default Accordion;
